import React from 'react';

export interface SeparatorProps {
  /**
   * Orientation of the separator
   * @default 'horizontal'
   */
  orientation?: 'horizontal' | 'vertical';
  /**
   * Visual style variant
   * @default 'solid'
   */
  variant?: 'solid' | 'dashed' | 'dotted';
  /**
   * Size/thickness of the separator
   * @default 'thin'
   */
  size?: 'thin' | 'medium' | 'thick';
  /**
   * Spacing around the separator
   * @default 'none'
   */
  spacing?: 'none' | 'small' | 'medium' | 'large';
  /**
   * Color variant
   * @default 'default'
   */
  color?: 'default' | 'light' | 'dark' | 'subtle';
  /**
   * Additional CSS classes
   */
  className?: string;
}

const Separator: React.FC<SeparatorProps> = ({
  orientation = 'horizontal',
  variant = 'solid',
  size = 'thin',
  spacing = 'none',
  color = 'default',
  className = ''
}) => {
  // Orientation classes
  const orientationClasses = {
    horizontal: 'w-full',
    vertical: 'h-full w-px'
  };

  // Size/thickness classes
  const sizeClasses = {
    thin: orientation === 'horizontal' ? 'h-px' : 'w-px',
    medium: orientation === 'horizontal' ? 'h-0.5' : 'w-0.5',
    thick: orientation === 'horizontal' ? 'h-[2px]' : 'w-[2px]'
  };

  // Variant classes (border style)
  const variantClasses = {
    solid: 'border-solid',
    dashed: 'border-dashed',
    dotted: 'border-dotted'
  };

  // Spacing classes
  const spacingClasses = {
    none: '',
    small: orientation === 'horizontal' ? '~my-1/2' : '~mx-1/2',
    medium: orientation === 'horizontal' ? '~my-2/3' : '~mx-2/3',
    large: orientation === 'horizontal' ? '~my-4/6' : '~mx-4/6'
  };

  // Color classes
  const colorClasses = {
    default:
      'bg-gray-200 dark:bg-secondary-dark-500 border-gray-200 dark:border-secondary-dark-500',
    light: 'bg-gray-100 dark:bg-secondary-dark-400 border-gray-100 dark:border-secondary-dark-400',
    dark: 'bg-gray-300 dark:bg-secondary-dark-600 border-gray-300 dark:border-secondary-dark-600',
    subtle: 'bg-gray-50 dark:bg-secondary-dark-300 border-gray-50 dark:border-secondary-dark-300'
  };

  // Use border for dashed/dotted variants, background for solid
  const isBorderVariant = variant !== 'solid';
  const baseClasses = isBorderVariant
    ? `border ${variantClasses[variant]} ${colorClasses[color]}`
    : colorClasses[color];

  const combinedClasses = `
    ${orientationClasses[orientation]}
    ${sizeClasses[size]}
    ${spacingClasses[spacing]}
    ${baseClasses}
    ${className}
  `
    .trim()
    .replace(/\s+/g, ' ');

  return (
    <div
      className={combinedClasses}
      role='separator'
      aria-orientation={orientation}
    />
  );
};

export { Separator };
